/**
 * AccountDetails.js
 * =================
 * Component for displaying detailed account information from Memento API
 * 
 * Features:
 *   - Fetch account data from Memento API
 *   - Display account balances, resources, permissions
 *   - Show account creation date
 *   - Display staked resources (CPU, NET, RAM)
 * 
 * Author: Block Explorer Team
 * Date: February 2026
 */

import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Divider } from 'primereact/divider';
import moment from 'moment';
import './AccountDetails.css';

const AccountDetails = ({ visible, accountName, onHide, blockchainEndpoint }) => {
    const [loading, setLoading] = useState(false);
    const [accountData, setAccountData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (visible && accountName) {
            fetchAccountDetails();
        } else {
            // Reset state when dialog is closed
            setAccountData(null);
            setError(null);
        }
    }, [visible, accountName]);

    const fetchAccountDetails = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Fetch account info from blockchain API
            const response = await fetch(`${blockchainEndpoint}/v1/chain/get_account`, {
                method: 'POST',
                body: JSON.stringify({
                    account_name: accountName
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch account details (${response.status})`);
            }

            const data = await response.json();
            setAccountData(data);
        } catch (err) {
            console.error('Error fetching account details:', err);
            setError(err.message || 'Failed to load account information');
        } finally {
            setLoading(false);
        }
    };

    const formatBytes = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const formatMicroseconds = (us) => {
        if (!us || us === 0) return '0 μs';
        if (us < 1000) return `${us} μs`;
        if (us < 1000000) return `${(us / 1000).toFixed(2)} ms`;
        return `${(us / 1000000).toFixed(2)} s`;
    };

    const formatTokenAmount = (amount) => {
        if (!amount) return 'N/A';
        if (typeof amount === 'string') return amount;
        return `${amount.toFixed(4)} ${accountData?.core_liquid_balance?.split(' ')[1] || 'TOKEN'}`;
    };

    const renderAccountOverview = () => {
        if (!accountData) return null;

        return (
            <div className="account-overview">
                <Card className="info-card">
                    <h3><i className="pi pi-user"></i> Account Overview</h3>
                    <div className="info-grid">
                        <div className="info-item">
                            <span className="info-label">Account Name:</span>
                            <code className="info-value highlight-text">{accountData.account_name}</code>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Created:</span>
                            <span className="info-value">
                                {accountData.created ? moment(accountData.created).format('MMM DD, YYYY HH:mm:ss') : 'N/A'}
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Privileged:</span>
                            <Tag 
                                value={accountData.privileged ? 'Yes' : 'No'} 
                                severity={accountData.privileged ? 'success' : 'secondary'}
                            />
                        </div>
                        <div className="info-item">
                            <span className="info-label">Last Code Update:</span>
                            <span className="info-value">
                                {accountData.last_code_update ? moment(accountData.last_code_update).format('MMM DD, YYYY HH:mm:ss') : 'Never'}
                            </span>
                        </div>
                    </div>
                </Card>

                <Card className="info-card">
                    <h3><i className="pi pi-wallet"></i> Balances</h3>
                    <div className="balance-grid">
                        <div className="balance-item">
                            <span className="balance-label">Liquid Balance:</span>
                            <span className="balance-value">
                                {accountData.core_liquid_balance || '0.0000 TOKEN'}
                            </span>
                        </div>
                    </div>
                </Card>
            </div>
        );
    };

    const renderResources = () => {
        if (!accountData) return null;

        const cpuUsed = accountData.cpu_limit?.used || 0;
        const cpuMax = accountData.cpu_limit?.max || 0;
        const cpuAvailable = accountData.cpu_limit?.available || 0;
        const cpuPercent = cpuMax > 0 ? ((cpuUsed / cpuMax) * 100).toFixed(2) : 0;

        const netUsed = accountData.net_limit?.used || 0;
        const netMax = accountData.net_limit?.max || 0;
        const netAvailable = accountData.net_limit?.available || 0;
        const netPercent = netMax > 0 ? ((netUsed / netMax) * 100).toFixed(2) : 0;

        const ramUsed = accountData.ram_usage || 0;
        const ramQuota = accountData.ram_quota || 0;
        const ramPercent = ramQuota > 0 ? ((ramUsed / ramQuota) * 100).toFixed(2) : 0;

        return (
            <Card className="info-card resources-card">
                <h3><i className="pi pi-chart-bar"></i> Resources</h3>
                
                <div className="resource-section">
                    <h4><i className="pi pi-bolt"></i> CPU</h4>
                    <div className="resource-details">
                        <div className="resource-row">
                            <span>Used:</span>
                            <span className="resource-value">{formatMicroseconds(cpuUsed)}</span>
                        </div>
                        <div className="resource-row">
                            <span>Available:</span>
                            <span className="resource-value">{formatMicroseconds(cpuAvailable)}</span>
                        </div>
                        <div className="resource-row">
                            <span>Max:</span>
                            <span className="resource-value">{formatMicroseconds(cpuMax)}</span>
                        </div>
                        <div className="resource-bar">
                            <div 
                                className="resource-bar-fill cpu" 
                                style={{ width: `${cpuPercent}%` }}
                            ></div>
                        </div>
                        <span className="resource-percent">{cpuPercent}% used</span>
                    </div>
                </div>

                <Divider />

                <div className="resource-section">
                    <h4><i className="pi pi-wifi"></i> NET</h4>
                    <div className="resource-details">
                        <div className="resource-row">
                            <span>Used:</span>
                            <span className="resource-value">{formatBytes(netUsed)}</span>
                        </div>
                        <div className="resource-row">
                            <span>Available:</span>
                            <span className="resource-value">{formatBytes(netAvailable)}</span>
                        </div>
                        <div className="resource-row">
                            <span>Max:</span>
                            <span className="resource-value">{formatBytes(netMax)}</span>
                        </div>
                        <div className="resource-bar">
                            <div 
                                className="resource-bar-fill net" 
                                style={{ width: `${netPercent}%` }}
                            ></div>
                        </div>
                        <span className="resource-percent">{netPercent}% used</span>
                    </div>
                </div>

                <Divider />

                <div className="resource-section">
                    <h4><i className="pi pi-database"></i> RAM</h4>
                    <div className="resource-details">
                        <div className="resource-row">
                            <span>Used:</span>
                            <span className="resource-value">{formatBytes(ramUsed)}</span>
                        </div>
                        <div className="resource-row">
                            <span>Quota:</span>
                            <span className="resource-value">{formatBytes(ramQuota)}</span>
                        </div>
                        <div className="resource-bar">
                            <div 
                                className="resource-bar-fill ram" 
                                style={{ width: `${ramPercent}%` }}
                            ></div>
                        </div>
                        <span className="resource-percent">{ramPercent}% used</span>
                    </div>
                </div>
            </Card>
        );
    };

    const renderStakedResources = () => {
        if (!accountData || !accountData.self_delegated_bandwidth && !accountData.total_resources) return null;

        const resources = accountData.total_resources || {};
        const selfDelegated = accountData.self_delegated_bandwidth || {};

        return (
            <Card className="info-card">
                <h3><i className="pi pi-lock"></i> Staked Resources</h3>
                <div className="staked-grid">
                    <div className="staked-item">
                        <span className="staked-label">CPU Staked:</span>
                        <span className="staked-value">
                            {resources.cpu_weight || selfDelegated.cpu_weight || 'N/A'}
                        </span>
                    </div>
                    <div className="staked-item">
                        <span className="staked-label">NET Staked:</span>
                        <span className="staked-value">
                            {resources.net_weight || selfDelegated.net_weight || 'N/A'}
                        </span>
                    </div>
                </div>
            </Card>
        );
    };

    const renderPermissions = () => {
        if (!accountData || !accountData.permissions) return null;

        return (
            <Card className="info-card">
                <h3><i className="pi pi-key"></i> Permissions</h3>
                <DataTable 
                    value={accountData.permissions} 
                    className="permissions-table"
                    stripedRows
                >
                    <Column 
                        field="perm_name" 
                        header="Permission" 
                        body={(rowData) => <code>{rowData.perm_name}</code>}
                    />
                    <Column 
                        field="parent" 
                        header="Parent" 
                        body={(rowData) => <code>{rowData.parent}</code>}
                    />
                    <Column 
                        header="Required Authority" 
                        body={(rowData) => (
                            <span>
                                Threshold: {rowData.required_auth?.threshold || 0}
                                {rowData.required_auth?.keys?.length > 0 && (
                                    <span className="key-count"> ({rowData.required_auth.keys.length} key{rowData.required_auth.keys.length > 1 ? 's' : ''})</span>
                                )}
                            </span>
                        )}
                    />
                </DataTable>
            </Card>
        );
    };

    const dialogFooter = (
        <div style={{ marginTop: '0.5rem' }}>
            <Button label="Close" icon="pi pi-times" onClick={onHide} className="p-button-text" />
            <Button 
                label="Refresh" 
                icon="pi pi-refresh" 
                onClick={fetchAccountDetails} 
                loading={loading}
                style={{ marginLeft: '0.5rem', marginRight: '1rem' }}
            />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={
                <div className="dialog-header">
                    <i className="pi pi-user"></i>
                    <span>Account Details: {accountName}</span>
                </div>
            }
            footer={dialogFooter}
            style={{ width: '90vw', maxWidth: '1000px' }}
            maximizable
            modal
            className="account-details-dialog"
        >
            {loading && !accountData ? (
                <div className="loading-container">
                    <ProgressSpinner />
                    <p>Loading account information...</p>
                </div>
            ) : error ? (
                <div className="error-container">
                    <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: '#ef4444' }}></i>
                    <h3>Error Loading Account</h3>
                    <p>{error}</p>
                    <Button 
                        label="Try Again" 
                        icon="pi pi-refresh" 
                        onClick={fetchAccountDetails}
                    />
                </div>
            ) : accountData ? (
                <div className="account-details-content">
                    {renderAccountOverview()}
                    {renderResources()}
                    {renderStakedResources()}
                    {renderPermissions()}
                </div>
            ) : (
                <div className="no-data-container">
                    <p>No account data available</p>
                </div>
            )}
        </Dialog>
    );
};

export default AccountDetails;
